import { useState } from "react";
import axios from "axios";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const cfg = { withCredentials: true };
const VOICES = ["male", "female"];

export default function NarrateControls({ book, onChanged }) {
  const [picking, setPicking] = useState(false);
  const [voices, setVoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const job = book.job;
  const running = job?.status === "running";
  const remaining = VOICES.filter((v) => (book.narrated_pages?.[v] ?? 0) < book.page_count);
  const cost = voices.reduce((sum, v) => sum + (book.estimate_remaining?.[v] ?? 0), 0);

  const toggle = (v) => setVoices((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reader/books/${book.id}/narrate`, { voices }, cfg);
      toast.success(`Narrating ${book.title} — you can leave this page, it keeps going.`);
      setPicking(false);
      setVoices([]);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start narration.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    try {
      await axios.post(`${API}/admin/reader/books/${book.id}/narrate/cancel`, {}, cfg);
      toast.success("Narration stopped. Pages already done are kept (any page in progress will still finish).");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop narration.");
    }
  };

  if (running) {
    const pct = Math.round(((job.done + job.failed) / job.total) * 100);
    return (
      <div className="space-y-2" data-testid={`narrate-progress-${book.product_id}`}>
        <div className="flex items-center justify-between text-xs font-mono-ui">
          <span className="flex items-center gap-2 text-primary-gold"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Narrating {job.voices.join(" + ")}</span>
          <span className="text-soft">{job.done + job.failed}/{job.total}</span>
        </div>
        <div className="h-1.5 bg-white/10"><div className="h-full bg-primary-gold transition-all duration-700" style={{ width: `${pct}%` }} /></div>
        <button onClick={cancel} data-testid={`narrate-cancel-${book.product_id}`} className="flex items-center gap-2 text-xs text-soft hover:text-[#d6453a] transition-colors">
          <Square className="w-3 h-3" /> Stop
        </button>
      </div>
    );
  }

  if (!remaining.length) {
    return <p className="text-xs font-mono-ui text-[#4ade80]" data-testid={`narrate-complete-${book.product_id}`}>Fully narrated in both voices.</p>;
  }

  return (
    <div className="space-y-3">
      {job && job.status !== "running" && (
        <p className="text-[11px] font-mono-ui text-soft">
          Last run: {job.status}{job.failed ? ` · ${job.failed} pages failed (press again to retry)` : ""}
        </p>
      )}
      {!picking ? (
        <button onClick={() => setPicking(true)} data-testid={`narrate-open-${book.product_id}`} className="w-full flex items-center justify-center gap-2 border border-primary-gold/40 text-primary-gold hover:bg-primary-gold/10 px-4 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors">
          <Mic className="w-4 h-4" /> Narrate whole book
        </button>
      ) : (
        <div className="border border-white/10 p-4 space-y-3" data-testid={`narrate-picker-${book.product_id}`}>
          <p className="text-xs text-soft">Which voices should be prepared?</p>
          {VOICES.map((v) => (
            <label key={v} className={`flex items-center justify-between gap-3 text-sm ${remaining.includes(v) ? "cursor-pointer" : "opacity-40"}`}>
              <span className="flex items-center gap-3">
                <input type="checkbox" disabled={!remaining.includes(v)} checked={voices.includes(v)} onChange={() => toggle(v)} className="accent-[#D4AF37] w-4 h-4" data-testid={`narrate-voice-${book.product_id}-${v}`} />
                <span className="capitalize">{v}</span>
              </span>
              <span className="font-mono-ui text-xs text-soft">{remaining.includes(v) ? `≈ US$${book.estimate_remaining[v].toFixed(2)}` : "done"}</span>
            </label>
          ))}
          <div className="flex gap-2 pt-1">
            <button onClick={start} disabled={!voices.length || busy} data-testid={`narrate-start-${book.product_id}`} className="flex-1 bg-primary-gold text-[#0B0C10] font-mono-ui text-[11px] uppercase tracking-[0.15em] py-2.5 disabled:opacity-40">
              {busy ? "Starting…" : voices.length ? `Start · ≈ US$${cost.toFixed(2)}` : "Choose a voice"}
            </button>
            <button onClick={() => { setPicking(false); setVoices([]); }} className="px-3 text-xs text-soft hover:text-text-primary">Cancel</button>
          </div>
          <p className="text-[11px] text-soft leading-relaxed">Charged once to your Universal Key balance. Pages already narrated are skipped.</p>
        </div>
      )}
    </div>
  );
}
